/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import FaExclamationTriangle from 'react-icons/lib/fa/exclamation-triangle';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';
import { toTitleCase } from '~services/utils'; // eslint-disable-line

// Components
import CircleWidget from './CircleWidget';
import CompanyInfo from './CompanyInfo';
import ReportHeader from './ReportHeader';
import ReportRating from './ReportRating';
import Box, { Minifiable } from '../../components/Box';
import Button from '../../components/Button';

// Messages
import messages from './messages/ReportSummary_messages';

const propTypes = {
  report: PropTypes.object.isRequired,
  review: PropTypes.object,
  session: PropTypes.object.isRequired,
  reportEditable: PropTypes.object,
  setReportEditable: PropTypes.func,
  onLogin: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  dashMode: PropTypes.bool,
  hasPiaced: PropTypes.bool,
  saveReview: PropTypes.func,
  savePiace: PropTypes.func,
  locale: PropTypes.object,
};

class ReportSummary extends PureComponent {
  state = {
    listState: {
      alternativeNames: false,
    },
  };

  savePiace = () => {
    this.props.savePiace({
      country: this.props.report.country,
      identifier: this.props.report.identifier,
    });
  };

  handleTaxPrepaymentChange = () => {
    const { reportEditable } = this.props;
    const attributes = { ...this.props.reportEditable.attributes };

    const current = (
      reportEditable.attributes.tax_prepayment_override
      || reportEditable.registrations.tax_prepayment
    );

    if (current === 'yes') {
      attributes.tax_prepayment_override = 'no';
    } else {
      attributes.tax_prepayment_override = 'yes';
    }

    this.props.setReportEditable({ attributes });
  };

  get companyReliabilityIcon() {
    const { report: { first_level: firstLevel }} = this.props;

    let src;
    /* eslint-disable max-len */
    if (['bankrupt', 'business_ban', 'ceased'].indexOf(firstLevel) !== -1) {
      src = 'https://zpckit.s3.amazonaws.com/uploads/2019/11/first_level_stop.png';
    } else if ([
      'ok_luotettava_kumppani_registry',
      'ok_luotettava_kumppani',
    ].indexOf(firstLevel) !== -1) {
      src = 'https://zpckit.s3.amazonaws.com/uploads/2019/11/first_level_lk_ok.png';
    } else if ([
      'warning_responsibilities',
      'warning_credit_notice',
    ].indexOf(firstLevel) !== -1) {
      src = 'https://zpckit.s3.amazonaws.com/uploads/2019/11/first_level_notice.png';
    } else if (['ok', 'waiting_info'].indexOf(firstLevel) !== -1) {
      src = 'https://zpckit.s3.amazonaws.com/uploads/2019/11/first_level_ok.png';
    }
    /* eslint-enable max-len */

    if (src) {
      return (
        <ReliabilityIcon
          data-interpretation={firstLevel}
          id="company-first-level-interpretation-icon"
          src={src}
        />
      );
    }
    return null;
  }

  get companyReliabilityInfo() {
    const { report: { first_level: firstLevel }} = this.props;

    const knownLevels = [
      'bankrupt',
      'business_ban',
      'ceased',
      'ok',
      'ok_luotettava_kumppani',
      'ok_luotettava_kumppani_registry',
      'waiting_info',
      'warning_credit_notice',
      'warning_responsibilities',
    ];

    if (knownLevels.indexOf(firstLevel) !== -1) {
      return (
        <div
          data-interpretation={firstLevel}
          id="company-first-level-interpretation-description"
        >
          {firstLevel === 'bankrupt' && (
            <BankrupcyWarning>
              <FaExclamationTriangle />
              <FormattedMessage
                {...messages.companyIsBankrupt}
                info="Company is bankrupt"
              />
            </BankrupcyWarning>
          )}
          <FormattedHTMLMessage
            {...messages[firstLevel]}
            info="Company status explanation"
          />
          <Minifiable>
            <FormattedHTMLMessage
              {...messages[`${firstLevel}_details`]}
              info="Company status detailed explanation"
            />
          </Minifiable>
        </div>
      );
    }

    return (
      <h1>
        <FormattedHTMLMessage
          {...messages.unknown}
          info="Unknown company status"
        />
      </h1>
    );
  }

  renderTaxPrepaymentEditable(editable) {
    if (editable.registrations.tax_prepayment === 'no') {
      return null;
    }

    if (editable.attributes.tax_prepayment_override === 'no') {
      return (
        <TaxPrepayment>
          <FormattedMessage
            {...messages.taxPrepaymentInactive}
            info="Tax prepayment is inactive"
          />
          <Button onClick={this.handleTaxPrepaymentChange} wrap>
            <FormattedMessage
              {...messages.taxPrepaymentEnable}
              info="Enable tax prepayment"
            />
          </Button>
        </TaxPrepayment>
      );
    }
    return (
      <TaxPrepayment>
        <FormattedMessage
          {...messages.taxPrepaymentActive}
          info="Tax prepayment is active"
        />
        <Button color="red" onClick={this.handleTaxPrepaymentChange} wrap>
          <FormattedMessage
            {...messages.taxPrepaymentDisable}
            info="Disable tax prepayment"
          />
        </Button>
      </TaxPrepayment>
    );
  }

  render() {
    const {
      report,
      review,
      session,
      dashMode,
      editMode,
      reportEditable,
      saveReview,
      hasPiaced,
      onLogin,
      locale,
      setReportEditable,
    } = this.props;

    return (
      <Box
        headerLabel={<FormattedMessage { ...messages.header } />}
        id="statement-section-overview"
      >

        {(!dashMode && !report.portfolio_enabled) && (
          <ReportHeader
            hasPiaced={hasPiaced}
            partners={report.partners}
            piaceCount={report.piace_count}
            savePiace={this.savePiace}
          />
        )}

        {(!report.portfolio_enabled || dashMode) && (
          <CompanyInfo
            editMode={editMode}
            locale={locale}
            report={report}
            reportEditable={reportEditable}
            setReportEditable={setReportEditable}
          >
            {({
              auxiliaryNames,
              industry,
              localities,
              marketingName,
              primaryName,
            }) => (
              <React.Fragment>
                {primaryName}
                {marketingName}
                {industry}

                <Minifiable>
                  {localities}
                  {auxiliaryNames}
                </Minifiable>
              </React.Fragment>
            )}
          </CompanyInfo>
        )}

        <Reliability>
          {this.companyReliabilityIcon}

          <ReliabilityInfo>
            {this.companyReliabilityInfo}

            <ReportRating
              report={report}
              review={review}
              session={session}
              dashMode={dashMode}
              saveReview={saveReview}
              onLogin={onLogin}
              locale={locale}
            />

            {report.qualifications.some(
              q => q.slug === 'consumer_electrical_installation'
            ) && (
              <CircleWidget circleType="electrical_installation" />
            )}

            {(editMode && !!reportEditable) && (
              this.renderTaxPrepaymentEditable(reportEditable)
            )}
          </ReliabilityInfo>
        </Reliability>
      </Box>
    );
  }
}

ReportSummary.propTypes = propTypes;

const BankrupcyWarning = styled.div`
  align-items: center;
  color: ${props => props.theme['red-dark']};
  display: flex;
  font-size: ${props => props.theme['font-size-xlarge']};
  line-height: ${props => props.theme['line-height-xlarge']};
`;

const Reliability = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 720px) {
    flex-direction: row;
  }
`;

const ReliabilityIcon = styled.div`
  background-image: url(${props => props.src});
  background-repeat: no-repeat;
  background-size: contain;
  height: 300px;
  max-width: 260px;
  width: 100%;
`;

const ReliabilityInfo = styled.div`
  @media (min-width: 720px) {
    padding-left: 24px;
  }
`;

const TaxPrepayment = styled.div`
  margin: 24px 0px;

  & > *:first-child {
    display: block;
    margin-bottom: 12px;
  }
`;

export default ReportSummary;
